import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCategoriesQuery, useDeleteCategory } from "../api/queries";
import { CategoriesTour, useCategoriesTour } from "../tour";
import { CategoriesFilter } from "../ui/CategoriesFilter";
import { CategoriesTable } from "../ui/CategoriesTable";
import { CategoriesToolbar } from "../ui/CategoriesToolbar";

export function CategoriesPage() {
  const nav = useNavigate({ from: "/app/catalog/categories" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/catalog/categories",
  });
  const list = useCategoriesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canManage = useActionPermission(["catalog:create"]);
  const deleteCategory = useDeleteCategory();
  const { run, handleJoyrideCallback, startTour } = useCategoriesTour();

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບຫມວດມູ່",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຫມວດມູ່ນີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteCategory.run(id), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບຫມວດມູ່ສຳເລັດ",
        error: "ລຶບຫມວດມູ່ລົ້ມເຫຼວ",
      });
    }
  };

  // Helper function to get category name by ID
  const getCategoryLabel = (id: string | null): string => {
    if (!id) return "—";
    const category = list.data?.data.find((c) => c.id === id);
    if (!category) return id;
    return (
      (category.name as { lo?: string })?.lo ??
      (category.name as { en?: string })?.en ??
      category.slug
    );
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຫມວດມູ່" },
          ]}
        />
      </Header>
      <Main>
        <CategoriesToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/catalog/categories/new" })}
          onStartTour={startTour}
        />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CategoriesFilter />
          <CategoriesTable
            canManage={!!canManage}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/catalog/categories/$id/edit",
                params: { id: row.id },
              })
            }
            onDelete={handleDelete}
            getCategoryLabel={getCategoryLabel}
          />
        </div>
        <CategoriesTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
